package data.repository

import domain.repository.ThemeRepository
import data.storage.SecureStorage
import data.storage.StorageKeys
import data.mapper.ThemeMapper
import ui.theme.ThemeMode
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Implementation of ThemeRepository
 */
class ThemeRepositoryImpl(
    private val secureStorage: SecureStorage
) : ThemeRepository {
    
    private val _currentTheme = MutableStateFlow(ThemeMode.LIGHT)
    
    init {
        // Load initial theme on creation
        loadInitialTheme()
    }
    
    override fun observeTheme(): Flow<ThemeMode> {
        return _currentTheme.asStateFlow()
    }
    
    override suspend fun setTheme(theme: ThemeMode) {
        try {
            val storageValue = ThemeMapper.toStorage(theme)
            secureStorage.storePlain(StorageKeys.THEME_MODE, storageValue)
            _currentTheme.tryEmit(theme)
            println("🎨 ThemeRepo: Theme changed to $theme and persisted")
        } catch (e: Exception) {
            println("💥 ThemeRepo: Error saving theme: ${e.message}")
            // Still update the flow even if storage fails
            _currentTheme.tryEmit(theme)
        }
    }
    
    override suspend fun getCurrentTheme(): ThemeMode {
        return _currentTheme.value
    }
    
    private fun loadInitialTheme() {
        try {
            val savedTheme = secureStorage.getPlain(StorageKeys.THEME_MODE)
            val themeMode = ThemeMapper.fromStorage(savedTheme)
            _currentTheme.tryEmit(themeMode)
            println("🎨 ThemeRepo: Initial theme loaded: $themeMode")
        } catch (e: Exception) {
            println("💥 ThemeRepo: Error loading theme: ${e.message}")
            _currentTheme.tryEmit(ThemeMode.LIGHT)
        }
    }
}
